// src/components/RepairsManagersScorecard.jsx
import React, { useState, useMemo } from 'react';
import { MantineReactTable } from 'mantine-react-table';
import {
  Text,
  Paper,
  Box,
  Loader,
  Alert,
  Button,
  Anchor,
  Group,
  Center,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import useAuthenticatedData from '../../../../hooks/useAuthenticatedData';
import config from '../../../../config';
import MembershipPopup from '../MembershipPopup';
import SQLEditorPopup from '../SQLEditorPopup';

// Metrics array for Repairs scorecard based on the screenshot
// Metrics array for Repairs scorecard with lowercase property names
const metrics = [
  { key: 'header_plannedInventory', label: 'Planned Inventory Spend', isHeader: true },
  { key: 'plannedspend', label: 'Spend', formatter: (v) => `$${v.toLocaleString()}` },
  { key: 'plannedprojectedmarginpercent', label: 'Projected Margin %', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'plannedprojectedmargindollars', label: 'Projected Margin $', formatter: (v) => `$${v.toLocaleString()}` },

  { key: 'header_landedInventory', label: 'Landed Inventory Spend', isHeader: true },
  { key: 'landedspend', label: 'Spend', formatter: (v) => `$${v.toLocaleString()}` },
  { key: 'landedprojectedmarginpercent', label: 'Projected Margin %', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'landedprojectedmargindollars', label: 'Projected Margin $', formatter: (v) => `$${v.toLocaleString()}` },

  { key: 'header_revenueMargin', label: 'Revenue/Margin', isHeader: true },
  { key: 'revenue', label: 'Revenue', formatter: (v) => `$${v.toLocaleString()}` },
  { key: 'grossmarginpercent', label: 'Gross Margin %', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'grossmargindollars', label: 'Gross Margin $', formatter: (v) => `$${v.toLocaleString()}` },

  { key: 'header_inventoryHealth', label: 'Inventory Health', isHeader: true },
  { key: 'inventoryhealth0to90percent', label: '0-90', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'inventoryhealth91to180percent', label: '91-180', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'inventoryhealth181to365percent', label: '181-365', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'inventoryhealth365pluspercent', label: '365+', formatter: (v) => `${(v).toFixed(0)}%` },

  { key: 'header_newVendor', label: 'New Vendor Base Growth', isHeader: true },
  { key: 'newvendorspoacutal', label: '# of PO Actual', formatter: (v) => v },
  { key: 'newvendorspopercent', label: '# of PO Pace', formatter: (v) => `${(v).toFixed(0)}%` },
  { key: 'rfqcount', label: '# of RFQs', formatter: (v) => v },
];

// MantineReactTable styling overrides
const mantineTableStyles = {
  table: { border: '1px solid var(--mantine-color-default-border)' },
  th: { padding: '6px 8px' }, // Reduced padding for headers
  td: { padding: '4px 8px' }, // Reduced padding for cells
  root: {
    '& .mantine-Table-td': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      fontWeight: 700,
      paddingLeft: '8px',
      minHeight: 'unset', // Remove minimum height
      height: '28px', // Smaller row height
    },
    '& .mantine-Table-tr': {
      cursor: 'pointer',
    },
  },
};

export default function RepairsManagersScorecard() {
  // Default from = 7 days ago, to = today
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return d;
  });
  const [toDate, setToDate] = useState(() => new Date());

  // Track which period button is active
  const [activePeriod, setActivePeriod] = useState(null);

  // State for pop-up dialogs
  const [openMembership, setOpenMembership] = useState(false);
  const [openQuery, setOpenQuery] = useState(false);

  // Helper functions for date range buttons
  const getWeekToDate = () => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const startOfWeek = new Date(now);
    // Set to previous Sunday (or today if it's Sunday)
    startOfWeek.setDate(now.getDate() - dayOfWeek);
    return {
      from: startOfWeek,
      to: now
    };
  };

  const getMonthToDate = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      from: startOfMonth,
      to: now
    };
  };

  const getQuarterToDate = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
    const startOfQuarter = new Date(now.getFullYear(), quarterStartMonth, 1);
    return {
      from: startOfQuarter,
      to: now
    };
  };

  const getYearToDate = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    return {
      from: startOfYear,
      to: now
    };
  };

  // Handle button clicks
  const handleWTD = () => {
    const { from, to } = getWeekToDate();
    setFromDate(from);
    setToDate(to);
    setActivePeriod('WTD');
  };

  const handleMTD = () => {
    const { from, to } = getMonthToDate();
    setFromDate(from);
    setToDate(to);
    setActivePeriod('MTD');
  };

  const handleQTD = () => {
    const { from, to } = getQuarterToDate();
    setFromDate(from);
    setToDate(to);
    setActivePeriod('QTD');
  };

  const handleYTD = () => {
    const { from, to } = getYearToDate();
    setFromDate(from);
    setToDate(to);
    setActivePeriod('YTD');
  };

  // Custom handler for date picker to unselect period buttons
  const handleDateChange = (newDate, type) => {
    if (type === 'from') {
      setFromDate(newDate);
    } else {
      setToDate(newDate);
    }
    // Unselect period buttons when manual date is changed
    setActivePeriod(null);
  };

  // Format YYYY-MM-DD
  const fromParam = fromDate.toISOString().split('T')[0];
  const toParam = toDate.toISOString().split('T')[0];

  const url = `${config.apiBaseUrl}/RepairsManager/Scorecard?from=${fromParam}&to=${toParam}`;
  const { data, isLoading, error } = useAuthenticatedData(url);

  // Build columns + rows
  const { columns, rows, avgJoeCode, otherCode } = useMemo(() => {
    if (!data || !Array.isArray(data)) return { columns: [], rows: [], avgJoeCode: null, otherCode: null };

  // Find the Average Joe and Other rows
    const avgJoeCode = data.find((r) => r.isaveragejoe)?.ccode;
    const otherCode = data.find((r) => r.ccode === 'Other')?.ccode || 'Other';

    // Get list of purchasing team codes, exclude avgJoe and Other
    const allCodes = Array.from(
      new Set(data.filter((r) => r.onpurchasingteam && r.ccode !== otherCode).map((r) => r.ccode))
    );

    // Sort codes with avgJoe and Other at the end
    const sortedCodes = [
      ...allCodes.filter((c) => c !== avgJoeCode),
      ...(avgJoeCode ? [avgJoeCode] : []),
      otherCode
    ];

    // Columns: metric + one per person
    const cols = [
      {
        accessorKey: 'metric',
        header: 'Metrics',
        size: 180, // Reduced from 200
        enablePinning: true,
        enableSorting: false,
        enableColumnFilter: false,
      },
      ...sortedCodes.map((code) => ({
        accessorKey: code,
        header: data.find((r) => r.ccode === code)?.name || code,
        size: 120, // Reduced from 140
        enableSorting: false,
        enableColumnFilter: false,
      })),
    ];

    // Pivot rows
    const pivotRows = metrics.map((m) => {
      const row = {
        id: m.key,
        metric: m.label,
        isHeader: m.isHeader || false
      };

      if (!m.isHeader) {
        sortedCodes.forEach((code) => {
          const rep = data.find((r) => r.ccode === code) || {};
          let raw = rep[m.key];
          row[code] = raw != null ? (m.formatter ? m.formatter(raw) : raw) : '-';
        });
      } else {
        // For header rows, leave cells empty
        sortedCodes.forEach((code) => {
          row[code] = '';
        });
      }

      return row;
    });

    return { columns: cols, rows: pivotRows, avgJoeCode, otherCode };
  }, [data]);

  return (
    <Paper shadow="md" p="lg" style={{ maxWidth: '100%', marginTop: '32px' }}>
      <Text
        component="h1"
        style={{
          fontFamily: 'Orbitron, sans-serif',
          color: '#FDB813',
          fontStyle: 'italic',
          fontSize: '1.5rem',
          marginBottom: '16px',
          textAlign: 'left'
        }}
      >
        Repairs Managers Scorecard
      </Text>

      {/* Technical options: small links for debugging */}
      <Group mb="sm" gap="sm">
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=repairs_managers_scorecard',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Query
        </Anchor>
      </Group>

      {/* Date range controls */}
      <Box my="md">
        <Group mb="sm">
          <Button
            onClick={handleWTD}
            variant={activePeriod === 'WTD' ? 'filled' : 'outline'}
            size="sm"
          >
            Week-to-Date
          </Button>
          <Button
            onClick={handleMTD}
            variant={activePeriod === 'MTD' ? 'filled' : 'outline'}
            size="sm"
          >
            Month-to-Date
          </Button>
          <Button
            onClick={handleQTD}
            variant={activePeriod === 'QTD' ? 'filled' : 'outline'}
            size="sm"
          >
            Quarter-to-Date
          </Button>
          <Button
            onClick={handleYTD}
            variant={activePeriod === 'YTD' ? 'filled' : 'outline'}
            size="sm"
          >
            Year-to-Date
          </Button>
        </Group>

        {/* Date pickers */}
        <Group gap="md">
          <DateInput
            label="From"
            value={fromDate}
            onChange={(newVal) => handleDateChange(newVal, 'from')}
            size="sm"
            style={{ width: 160 }}
          />
          <DateInput
            label="To"
            value={toDate}
            onChange={(newVal) => handleDateChange(newVal, 'to')}
            size="sm"
            style={{ width: 160 }}
          />
        </Group>
      </Box>

      {/* Table container */}
      <Paper shadow="md" p="md" style={{ height: 600, width: '100%' }}>
        <Box style={{ height: '100%' }}>
          {isLoading ? (
            <Center style={{ height: '100%' }}>
              <Group gap="sm">
                <Loader />
                <Text>Loading...</Text>
              </Group>
            </Center>
          ) : error ? (
            <Alert color="red" title="Error">
              {error.message}
            </Alert>
          ) : (
            <MantineReactTable
              columns={columns}
              data={rows}
              enablePinning
              initialState={{
                columnPinning: {
                  left: ['metric'],
                  right: [avgJoeCode, otherCode].filter(Boolean),
                },
                pagination: {
                  pageSize: 50,
                },
                density: 'xs', // Set table density to extra small
              }}
              enableTopToolbar={false} // Disable toolbar to save space
              mantineTableProps={{
               
                highlightOnHover: true,
                withBorder: true,
                styles: mantineTableStyles,
              }}
              mantineTableHeadProps={{
                style: { height: '32px' }, // Smaller header height
              }}
              enableGlobalFilter={false} // Disable global filter to save space
              enableColumnFilters={false}
              enableSorting={false}
              enableRowSelection={false}
              getRowId={(row) => row.id}
              mantineTableBodyRowProps={({ row }) => ({
                style: {
                  cursor: 'pointer',
                  backgroundColor: row.original.isHeader ? 'var(--mantine-color-dark-6)' : undefined,
                  color: row.original.isHeader ? 'var(--mantine-color-white)' : undefined,
                  fontWeight: row.original.isHeader ? 'bold' : undefined,
                  height: row.original.isHeader ? '28px' : '26px', // Reduced heights
                },
              })}
              mantineTableBodyCellProps={({ cell }) => ({
                style: {
                  backgroundColor: cell.row.original.isHeader ? 'var(--mantine-color-dark-6)' : undefined,
                  color: cell.row.original.isHeader ? 'var(--mantine-color-white)' : undefined,
                  padding: '2px 6px', // Reduced cell padding
                },
              })}
              enablePagination={false} // Disable pagination to save space
              enableExport={false} // Disable export to save space
            />
          )}
        </Box>
      </Paper>

      {/* Pop-up dialogs */}
      <MembershipPopup opened={openMembership} onClose={() => setOpenMembership(false)} />
      <SQLEditorPopup
        opened={openQuery}
        onClose={() => setOpenQuery(false)}
        title="SQL Query"
        endpoint={`${config.apiBaseUrl}/RepairsgManager/Scorecard/queries/managers-scorecard-query`}
      />
    </Paper>
  );
}